import { DateTime, Duration } from 'luxon';
import { Component, TelemetryPlatform, } from '@lucidlink/dto/validation';
import { PublishTelemetryDto } from '@lucidlink/dto/web-service-api';
import { exponentialBackoffFullJitter, resolveAfter, retryUntilNoException, } from '@lucidlink/tscore';
import { Counter } from '#sdk/utils/counter';
const mapPlatform = (platform) => {
    switch (platform) {
        case 'Android':
            return TelemetryPlatform.ANDROID;
        case 'iOS':
            return TelemetryPlatform.IOS;
        case 'Windows':
            return TelemetryPlatform.WIN;
        case 'Linux':
            return TelemetryPlatform.LIN;
        case 'OSX':
            return TelemetryPlatform.MACOS;
        case 'TestPlatform': // Only used for testing
            return TelemetryPlatform.LIN;
        case 'Web':
        case 'NodeJS':
        case 'Other':
            return TelemetryPlatform.OTHER;
        default:
            throw new Error('unreachable');
    }
};
const mapComponent = (app) => {
    switch (app) {
        case 'LucidDaemon':
            return Component.DAEMON;
        case 'LucidDesktopApp':
            return Component.DESKTOP_CLIENT;
        case 'LucidWebApp':
            return Component.WEB_CLIENT;
        case 'LucidWebService':
            return Component.WEB_SERVICE;
        case 'LucidAndroidApp':
        case 'LucidIOSApp':
            return Component.MOBILE_CLIENT;
        case 'LucidPythonSDKBeta':
            return Component.PYTHON_SDK;
        case 'LucidTestApp': // Only used for testing
            return Component.DAEMON;
        case 'LucidSdk':
            return Component.SDK;
        case 'LucidApi':
            return Component.API;
        default:
            throw new Error('unreachable');
    }
};
export const createTelemetryClient = ({ logger, getFetcher, additionalInfo, commonData, }) => {
    const childLogger = logger.child({ class: 'TelemetryClient' });
    const component = mapComponent(additionalInfo.application);
    const componentVersion = additionalInfo.componentVersion;
    const platform = mapPlatform(additionalInfo.platform);
    const platformVersion = additionalInfo.platformVersion;
    const webBrowser = additionalInfo.webBrowser;
    return new TelemetryClientImpl(new Counter(), childLogger, getFetcher, {
        platform,
        platformVersion,
        component,
        componentVersion,
        webBrowser,
        ...commonData,
    });
};
class TelemetryClientImpl {
    outgoingTelemetryEvents;
    logger;
    getFetcher;
    commonData;
    constructor(outgoingTelemetryEvents, logger, getFetcher, commonData) {
        this.outgoingTelemetryEvents = outgoingTelemetryEvents;
        this.logger = logger;
        this.getFetcher = getFetcher;
        this.commonData = commonData;
    }
    sendEvent = ({ name, data }) => {
        this.outgoingTelemetryEvents.increment();
        this.sendEventInternal(name, {
            clientEventTime: DateTime.local().toISO(),
            ...data,
        })
            .catch((e) => {
            this.logger.error(`Could not send telemetry event: ${JSON.stringify(e)}`);
        })
            .finally(() => this.outgoingTelemetryEvents.decrement());
    };
    createSubClient({ getFetcher, commonData, }) {
        return new TelemetryClientImpl(this.outgoingTelemetryEvents, this.logger, getFetcher, {
            ...this.commonData,
            ...commonData,
        });
    }
    sendEventInternal = async (name, data) => {
        return retryUntilNoException(async () => {
            await this.getFetcher().post(['telemetry', 'events'], {
                body: PublishTelemetryDto.create({
                    events: [
                        {
                            eventName: name,
                            ...this.commonData,
                            ...data,
                        },
                    ],
                    clientUploadTime: DateTime.local().toISO(),
                }),
            });
        }, {
            retries: Number.POSITIVE_INFINITY,
            sleep: telemetryBackoff,
            onRetry: ({ delayMs, error }) => {
                const message = error instanceof Error ? error.message : String(error);
                this.logger.warn(`Failed to flush telemetry: ${message}, retrying after ${delayMs}ms.`);
            },
        });
    };
    waitOutgoingEvents = async (timeoutInMs) => {
        const waitLoopDelay = 100; // milliseconds
        const startTime = Date.now();
        while (this.outgoingTelemetryEvents.value() !== 0) {
            const currentTime = Date.now();
            if (startTime + timeoutInMs <= currentTime) {
                return false;
            }
            await resolveAfter(waitLoopDelay);
        }
        return this.outgoingTelemetryEvents.value() === 0;
    };
}
const telemetryBackoff = exponentialBackoffFullJitter({
    delay: Duration.fromObject({ seconds: 1 }).toMillis(),
    maxDelay: Duration.fromObject({ minutes: 1 }).toMillis(),
});
